import { useId, useState } from "react";
import { Markdown } from "@client/components/Markdown";
import { Button, Textarea } from "@client/components/ui";
import { useT } from "@client/i18n";
import type { PrepLecture } from "@content/prepSchema";
import { AnchorLine } from "./AnchorLine";
import { BookBox } from "./BookBox";
import { Panel } from "./Panel";
import { ShowInModelButton } from "./ShowInModel";

type Lecture = Extract<PrepLecture, { kind: "lecture" }>;
type Step = Lecture["derivation"]["steps"][number];

function StepCard({
  step,
  revealed,
  onToggle,
  onShow,
}: {
  step: Step;
  revealed: boolean;
  onToggle(): void;
  onShow(presetId: string): void;
}) {
  const t = useT();
  const id = useId();
  // The teacher's own attempt is local scratch space and is deliberately not persisted.
  const [attempt, setAttempt] = useState("");
  return (
    <li className="space-y-3 rounded-lg border border-ink-200 p-3 dark:border-ink-700">
      <h3 id={`${id}-title`} className="font-medium text-ink-900 dark:text-ink-50">
        {step.title}
      </h3>
      <Markdown variant="compact">{step.prompt}</Markdown>
      <Textarea
        label={t("teacher.prep.attempt")}
        rows={3}
        value={attempt}
        onChange={(event) => setAttempt(event.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          aria-expanded={revealed}
          aria-controls={`${id}-answer`}
          aria-describedby={`${id}-title`}
          onClick={onToggle}
        >
          {revealed ? t("teacher.prep.hide") : t("teacher.prep.show")}
        </Button>
        <ShowInModelButton preset={step.preset} onShow={onShow} />
      </div>
      <div id={`${id}-answer`} hidden={!revealed} className="min-w-0 space-y-2">
        {revealed && <Markdown>{step.answer}</Markdown>}
      </div>
      <AnchorLine book={step.book} beyondBook={step.beyondBook} />
    </li>
  );
}

export function DerivationPanel({
  derivation,
  revealed,
  onToggle,
  onShow,
  className,
}: {
  derivation: Lecture["derivation"];
  revealed: ReadonlySet<string>;
  onToggle(stepId: string): void;
  onShow(presetId: string): void;
  className?: string;
}) {
  const t = useT();
  return (
    <Panel title={t("teacher.prep.panel.derivation")} {...(className ? { className } : {})}>
      <BookBox>{derivation.bookBox}</BookBox>
      <ol className="space-y-3">
        {derivation.steps.map((step) => (
          <StepCard
            key={step.id}
            step={step}
            revealed={revealed.has(step.id)}
            onToggle={() => onToggle(step.id)}
            onShow={onShow}
          />
        ))}
      </ol>
    </Panel>
  );
}
